import { useDarkMode } from "../ThemeContext";
import ThemedReaderFrame from "../components/ThemedReaderFrame";
import React, { useState, useRef, useCallback, useEffect } from "react";
import bhajanData from "../data/bhajan.json";
import plistData from "../data/plist.json";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";

const bhajanList = Object.values(bhajanData);
const BHAJAN_BY_SID = new Map(bhajanList.map((b) => [b.sid, b]));

// pid is the actual plist value used in bhajan.json
const CATEGORIES = Object.values(plistData).map((p) => ({
  plist: p.pid,
  label: p.title,
}));

const LAST_READ_KIRTAN_KEY = "kirtan-last-read";

function writeCookie(name, value, days = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const pair = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : "";
}

function saveLastReadKirtan(bhajan) {
  const payload = JSON.stringify({
    sid: bhajan.sid,
    subtitle: bhajan.subtitle,
  });
  try {
    localStorage.setItem(LAST_READ_KIRTAN_KEY, payload);
  } catch {
    // no-op
  }
  writeCookie(LAST_READ_KIRTAN_KEY, payload);
}

function loadLastReadKirtan() {
  let raw = "";
  try {
    raw = localStorage.getItem(LAST_READ_KIRTAN_KEY) || "";
  } catch {
    // no-op
  }
  if (!raw) raw = readCookie(LAST_READ_KIRTAN_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.sid) return null;
    return parsed;
  } catch {
    return null;
  }
}

const ROMAN_WORD_OVERRIDES = {
  gunatit: "ગુણાતીત",
  gunatīt: "ગુણાતીત",
};

const ROMAN_CONSONANTS = {
  ksh: "ક્ષ",
  chh: "છ",
  kh: "ખ",
  gh: "ઘ",
  ch: "ચ",
  jh: "ઝ",
  th: "થ",
  dh: "ધ",
  ph: "ફ",
  bh: "ભ",
  sh: "શ",
  gn: "જ્ઞ",
  tr: "ત્ર",
  ny: "ન્ય",
  ng: "ઙ",
  k: "ક",
  g: "ગ",
  c: "ક",
  j: "જ",
  t: "ત",
  d: "દ",
  n: "ન",
  p: "પ",
  b: "બ",
  m: "મ",
  y: "ય",
  r: "ર",
  l: "લ",
  v: "વ",
  w: "વ",
  s: "સ",
  h: "હ",
  f: "ફ",
  q: "ક",
  x: "ક્સ",
  z: "ઝ",
  "~": "ળ",
  "^": "ટ",
};

const ROMAN_VOWELS = {
  aa: { independent: "આ", matra: "ા" },
  ai: { independent: "ઐ", matra: "ૈ" },
  au: { independent: "ઔ", matra: "ૌ" },
  ii: { independent: "ઈ", matra: "ી" },
  ee: { independent: "ઈ", matra: "ી" },
  oo: { independent: "ઊ", matra: "ૂ" },
  uu: { independent: "ઊ", matra: "ૂ" },
  a: { independent: "અ", matra: "" },
  i: { independent: "ઇ", matra: "િ" },
  u: { independent: "ઉ", matra: "ુ" },
  e: { independent: "એ", matra: "ે" },
  o: { independent: "ઓ", matra: "ો" },
};

const GUJARATI_PHONETIC_EQUIVALENTS = [
  [/િ|ી/g, "િ"],
  [/ુ|ૂ/g, "ુ"],
  [/ે|ૈ/g, "ે"],
  [/ો|ૌ/g, "ો"],
  [/ઁ|ં|ઃ/g, "ં"],
  [/શ|ષ|સ/g, "સ"],
  [/ળ/g, "લ"],
];

const GUJARATI_INDEPENDENT_VOWELS = {
  અ: "a",
  આ: "aa",
  ઇ: "i",
  ઈ: "i",
  ઉ: "u",
  ઊ: "u",
  ઋ: "ri",
  એ: "e",
  ઐ: "ai",
  ઓ: "o",
  ઔ: "au",
};

const GUJARATI_MATRAS = {
  "ા": "aa",
  "િ": "i",
  "ી": "i",
  "ુ": "u",
  "ૂ": "u",
  "ૃ": "ri",
  "ે": "e",
  "ૈ": "ai",
  "ો": "o",
  "ૌ": "au",
};

const GUJARATI_CONSONANTS = {
  ક: "k",
  ખ: "kh",
  ગ: "g",
  ઘ: "gh",
  ઙ: "ng",
  ચ: "ch",
  છ: "chh",
  જ: "j",
  ઝ: "jh",
  ઞ: "ny",
  ટ: "t",
  ઠ: "th",
  ડ: "d",
  ઢ: "dh",
  ણ: "n",
  ત: "t",
  થ: "th",
  દ: "d",
  ધ: "dh",
  ન: "n",
  પ: "p",
  ફ: "ph",
  બ: "b",
  ભ: "bh",
  મ: "m",
  ય: "y",
  ર: "r",
  લ: "l",
  વ: "v",
  શ: "sh",
  ષ: "sh",
  સ: "s",
  હ: "h",
  ળ: "l",
};

const GUJARATI_SIGNS = {
  "ં": "m",
  "ઃ": "h",
  "ઁ": "m",
};

const GUJARATI_VIRAMA = "્";

function getSnippet(text, word) {
  const idx = text.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1) return text.slice(0, 120).trim() + "…";
  const start = Math.max(0, idx - 80);
  const end = Math.min(text.length, idx + word.length + 80);
  let snippet = text.slice(start, end).trim();
  if (start > 0) snippet = "…" + snippet;
  if (end < text.length) snippet += "…";
  return snippet;
}

function getSnippetForCandidates(content, candidates) {
  const lower = content.toLowerCase();
  let best = null;

  candidates.forEach((candidate) => {
    if (!candidate) return;
    const idx = lower.indexOf(candidate.toLowerCase());
    if (idx === -1) return;
    if (!best || idx < best.idx) {
      best = { idx, candidate };
    }
  });

  if (!best) return { snippet: "", hit: "" };
  return {
    snippet: getSnippet(content, best.candidate),
    hit: best.candidate,
  };
}

function isGujaratiText(text) {
  return /[\u0A80-\u0AFF]/.test(text || "");
}

function normalizeGujaratiPhonetic(text) {
  let normalized = (text || "").toLowerCase().replace(/\s+/g, "");
  GUJARATI_PHONETIC_EQUIVALENTS.forEach(([pattern, replacement]) => {
    normalized = normalized.replace(pattern, replacement);
  });
  return normalized;
}

function normalizeRomanPhonetic(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/aa/g, "a")
    .replace(/ee|ii/g, "i")
    .replace(/oo|uu/g, "u")
    .replace(/sh/g, "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/chh/g, "ch")
    .replace(/v/g, "w");
}

function gujaratiToRomanPhonetic(text) {
  const input = text || "";
  let out = "";

  for (let i = 0; i < input.length; i += 1) {
    const c = input[i];
    const next = input[i + 1] || "";

    if (c === "ક" && next === "્" && input[i + 2] === "ષ") {
      out += "ksh";
      i += 2;
      continue;
    }
    if (c === "જ" && next === "્" && input[i + 2] === "ઞ") {
      out += "gn";
      i += 2;
      continue;
    }

    if (GUJARATI_INDEPENDENT_VOWELS[c]) {
      out += GUJARATI_INDEPENDENT_VOWELS[c];
      continue;
    }

    if (GUJARATI_CONSONANTS[c]) {
      const base = GUJARATI_CONSONANTS[c];
      const nextChar = input[i + 1] || "";
      if (GUJARATI_MATRAS[nextChar]) {
        out += base + GUJARATI_MATRAS[nextChar];
        i += 1;
        continue;
      }
      if (nextChar === GUJARATI_VIRAMA) {
        out += base;
        i += 1;
        continue;
      }
      out += base + "a";
      continue;
    }

    if (GUJARATI_SIGNS[c]) {
      out += GUJARATI_SIGNS[c];
      continue;
    }

    out += " ";
  }

  out = out.replace(/a(\s|$)/g, "$1");
  return normalizeRomanPhonetic(out);
}

function transliterateRomanWordToGujarati(word) {
  const override = ROMAN_WORD_OVERRIDES[(word || "").toLowerCase()];
  if (override) return override;

  const input = (word || "")
    .replace(/L/g, "~")
    .replace(/T/g, "^")
    .toLowerCase()
    .replace(/[^a-z~^]/g, "");
  if (!input) return "";

  const vowels = Object.keys(ROMAN_VOWELS).sort((a, b) => b.length - a.length);
  const consonants = Object.keys(ROMAN_CONSONANTS).sort(
    (a, b) => b.length - a.length,
  );

  let out = "";
  let i = 0;
  while (i < input.length) {
    const remaining = input.slice(i);

    let vowelToken = "";
    for (const v of vowels) {
      if (remaining.startsWith(v)) {
        vowelToken = v;
        break;
      }
    }
    if (vowelToken) {
      out += ROMAN_VOWELS[vowelToken].independent;
      i += vowelToken.length;
      continue;
    }

    let consonantToken = "";
    for (const c of consonants) {
      if (remaining.startsWith(c)) {
        consonantToken = c;
        break;
      }
    }
    if (!consonantToken) {
      i += 1;
      continue;
    }

    out += ROMAN_CONSONANTS[consonantToken];
    i += consonantToken.length;

    const afterConsonant = input.slice(i);
    let followingVowel = "";
    for (const v of vowels) {
      if (afterConsonant.startsWith(v)) {
        followingVowel = v;
        break;
      }
    }
    if (followingVowel) {
      out += ROMAN_VOWELS[followingVowel].matra;
      i += followingVowel.length;
    }
  }

  return out;
}

function generateGujaratiLVariants(text) {
  const source = text || "";
  if (!/[લળ]/.test(source)) return [source];

  let variants = [""];
  for (const ch of source) {
    if (ch === "લ" || ch === "ળ") {
      const next = [];
      variants.forEach((prefix) => {
        next.push(prefix + "લ");
        next.push(prefix + "ળ");
      });
      variants = next;
      continue;
    }
    variants = variants.map((prefix) => prefix + ch);
  }
  return Array.from(new Set(variants));
}

function buildWordCandidates(query) {
  const q = (query || "").trim();
  if (!q) return [];

  const candidates = new Set([q]);
  const lowered = q.toLowerCase();

  if (!isGujaratiText(q) && /^[a-z\s]+$/i.test(q)) {
    const transliterated = q
      .split(/\s+/)
      .map((w) => transliterateRomanWordToGujarati(w))
      .filter(Boolean)
      .join(" ")
      .trim();
    if (transliterated) {
      generateGujaratiLVariants(transliterated).forEach((variant) => {
        candidates.add(variant);
        candidates.add(normalizeGujaratiPhonetic(variant));
      });
    }
    if (ROMAN_WORD_OVERRIDES[lowered]) {
      candidates.add(ROMAN_WORD_OVERRIDES[lowered]);
      candidates.add(normalizeGujaratiPhonetic(ROMAN_WORD_OVERRIDES[lowered]));
    }
  }

  if (isGujaratiText(q)) {
    candidates.add(normalizeGujaratiPhonetic(q));
  }

  return Array.from(candidates).filter(Boolean);
}

function Highlighted({ text, word }) {
  if (!(word || "").trim()) return <>{text}</>;
  const regex = new RegExp(
    `(${(word || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <mark key={i}>{part}</mark> : part,
      )}
    </>
  );
}

export default function Kirtan() {
  const [selectedPlist, setSelectedPlist] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [serialSearch, setSerialSearch] = useState("");

  const [wordQuery, setWordQuery] = useState("");
  const [wordResults, setWordResults] = useState(null);
  const [wordSearching, setWordSearching] = useState(false);
  const [resultHighlightWord, setResultHighlightWord] = useState("");
  const [lastReadKirtan, setLastReadKirtan] = useState(() =>
    loadLastReadKirtan(),
  );
  const [darkMode, setDarkMode] = useDarkMode();
  const indexRef = useRef(null);
  const iframeRef = useRef(null);

  const loadAndSearch = useCallback(async (query) => {
    if (!query.trim()) return;
    setWordSearching(true);
    setWordResults(null);
    try {
      if (!indexRef.current) {
        const res = await fetch(
          `${process.env.PUBLIC_URL}/data/kirtan-index.json`,
        );
        if (!res.ok) throw new Error("Could not load search index");
        indexRef.current = await res.json();
      }
      const ql = query.trim().toLowerCase();
      const queryRomanNormalized = normalizeRomanPhonetic(query);
      const queryIsRoman =
        !isGujaratiText(query) && /^[a-z\s]+$/i.test(query.trim());
      const candidates = buildWordCandidates(query);
      const normalizedCandidates = candidates.map((c) =>
        normalizeGujaratiPhonetic(c),
      );
      const results = [];
      for (const entry of indexRef.current) {
        const contentLower = entry.content.toLowerCase();
        const contentPhonetic = normalizeGujaratiPhonetic(entry.content);
        if (!entry._romanPhonetic) {
          entry._romanPhonetic = gujaratiToRomanPhonetic(entry.content);
        }

        let matched = false;
        for (let i = 0; i < candidates.length; i += 1) {
          const c = candidates[i];
          const normalized = normalizedCandidates[i];
          if (
            (c && contentLower.includes(c.toLowerCase())) ||
            (normalized && contentPhonetic.includes(normalized))
          ) {
            matched = true;
            break;
          }
        }

        if (
          !matched &&
          queryIsRoman &&
          queryRomanNormalized &&
          entry._romanPhonetic.includes(queryRomanNormalized)
        ) {
          matched = true;
        }

        if (!matched && !contentLower.includes(ql)) continue;

        const bhajan = BHAJAN_BY_SID.get(entry.sid);
        if (!bhajan) continue;
        const { snippet, hit } = getSnippetForCandidates(entry.content, [
          ...candidates,
          query.trim(),
        ]);
        results.push({
          bhajan,
          snippet,
          hit: hit || query.trim(),
        });
      }
      setWordResults(results);
      setResultHighlightWord(results[0]?.hit || query.trim());
    } catch (err) {
      setWordResults([]);
      setResultHighlightWord(query.trim());
      console.error(err);
    } finally {
      setWordSearching(false);
    }
  }, []);

  const handleWordSearch = (e) => {
    e.preventDefault();
    loadAndSearch(wordQuery);
  };

  const clearWordSearch = () => {
    setWordQuery("");
    setWordResults(null);
    setResultHighlightWord("");
  };

  const loadBhajan = useCallback((bhajan) => {
    setSelected(bhajan);
    setLastReadKirtan({ sid: bhajan.sid, subtitle: bhajan.subtitle });
    saveLastReadKirtan(bhajan);
  }, []);

  const handleResultClick = (bhajan) => {
    loadBhajan(bhajan);
  };

  const filtered = bhajanList.filter((b) => {
    if (selectedPlist && b.plist !== selectedPlist) return false;
    if (search) {
      const byText =
        b.subtitle.toLowerCase().includes(search.toLowerCase()) ||
        b.title.includes(search);
      if (!byText) return false;
    }
    if (serialSearch.trim()) {
      if (!String(b.sid).includes(serialSearch.trim())) return false;
    }
    return true;
  });

  const handleCategoryClick = (plist) => {
    setSelectedPlist(plist);
    setSelected(null);
  };

  const openLastReadKirtan = useCallback(() => {
    if (!lastReadKirtan) return;
    const bhajan = BHAJAN_BY_SID.get(lastReadKirtan.sid);
    if (bhajan) loadBhajan(bhajan);
  }, [lastReadKirtan, loadBhajan]);

  const iframeSrc = selected
    ? `${process.env.PUBLIC_URL}/data/bhajan/${selected.lyrics}`
    : null;

  const hasAudio = selected && selected.song && selected.song !== "NULL";
  const contentHighlightWord =
    wordResults !== null ? (resultHighlightWord || wordQuery).trim() : "";

  const applyHighlightToIframe = useCallback((word) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc || !doc.body) return;

    if (!doc.body.dataset.originalHtml) {
      doc.body.dataset.originalHtml = doc.body.innerHTML;
    }

    const baseHtml = doc.body.dataset.originalHtml;
    if (!word) {
      doc.body.innerHTML = baseHtml;
      return;
    }

    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    doc.body.innerHTML = baseHtml.replace(
      regex,
      '<mark style="background:#ffe066;color:#222;border-radius:2px">$1</mark>',
    );
  }, []);

  useEffect(() => {
    applyHighlightToIframe(contentHighlightWord);
  }, [applyHighlightToIframe, contentHighlightWord, selected?.sid]);

  return (
    <main
      className={`page-content vachanamrut-page swamini-vato-page scripture-reader${darkMode ? " sv-dark" : ""}`}
    >
      <section className="page-header">
        <h1 className="section-title">
          Kirtan
          <span className="reader-muted" style={{ fontSize: "1rem", marginLeft: "12px", color: "#888" }}>
            ({filtered.length})
          </span>
        </h1>
        <p className="header-subtitle">કીર્તન · ભજન · પ્રાર્થના</p>
        {lastReadKirtan && (
          <button
            type="button"
            className="last-read-vat"
            onClick={openLastReadKirtan}
            title="Open last read kirtan"
          >
            Last Read Kirtan: {lastReadKirtan.subtitle || `#${lastReadKirtan.sid}`}
          </button>
        )}
      </section>

      {/* ── Full-text word search ── */}
      <form className="word-search-bar" onSubmit={handleWordSearch}>
        <input
          className="word-search-input"
          type="text"
          placeholder="Search word in all Kirtan / Bhajan… (Gujarati or English)"
          value={wordQuery}
          onChange={(e) => setWordQuery(e.target.value)}
        />
        <button
          type="submit"
          className="word-search-btn"
          disabled={wordSearching}
        >
          {wordSearching ? "Searching…" : "Search"}
        </button>
        <button
          type="button"
          className="word-search-clear"
          onClick={() => setDarkMode((d) => !d)}
          title="Toggle light/dark mode"
        >
          {darkMode ? "Light" : "Dark"}
        </button>
        {wordResults !== null && (
          <button
            type="button"
            className="word-search-clear"
            onClick={clearWordSearch}
          >
            ✕ Clear
          </button>
        )}
      </form>

      {/* ── Word search results ── */}
      {wordResults !== null && (
        <div className="word-results-panel">
          {wordResults.length === 0 ? (
            <p className="word-results-empty">
              No results found for "{wordQuery}".
            </p>
          ) : (
            <>
              <p className="word-results-count">
                {wordResults.length} result{wordResults.length !== 1 ? "s" : ""}{" "}
                for "{wordQuery}"
              </p>
              <ul className="word-results-list search-results-scroll-list">
                {wordResults.map(({ bhajan, snippet, hit }) => (
                  <li
                    key={bhajan.sid}
                    className={`word-result-item${selected?.sid === bhajan.sid ? " active" : ""}`}
                    onClick={() => handleResultClick(bhajan)}
                  >
                    <div className="word-result-header">
                      <span className="vachan-name">{bhajan.subtitle}</span>
                      <span className="vachan-name-guj">{bhajan.title}</span>
                    </div>
                    <p className="word-result-snippet">
                      <Highlighted
                        text={snippet}
                        word={hit || resultHighlightWord || wordQuery}
                      />
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="vachanamrut-layout">
        {/* Category filter */}
        <div className="vachanamrut-chapters" role="group" aria-label="Categories">
          <button
            className={`chapter-btn${!selectedPlist ? " active" : ""}`}
            aria-pressed={!selectedPlist}
            onClick={() => {
              setSelectedPlist(null);
              setSelected(null);
            }}
          >
            All
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.plist}
              className={`chapter-btn${selectedPlist === cat.plist ? " active" : ""}`}
              aria-pressed={selectedPlist === cat.plist}
              onClick={() => handleCategoryClick(cat.plist)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* List + Content */}
        <div className="vachanamrut-body">
          <aside className="vachanamrut-list-panel">
            <input
              className="vachanamrut-search"
              type="text"
              placeholder="Filter list…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              className="vachanamrut-search vachanamrut-search-serial"
              type="text"
              placeholder="Serial no search (e.g. 12)"
              value={serialSearch}
              onChange={(e) => setSerialSearch(e.target.value)}
            />
            <ul className="vachanamrut-list">
              {filtered.map((b) => (
                <li
                  key={b.sid}
                  className={`vachanamrut-item${selected?.sid === b.sid ? " active" : ""}`}
                  aria-current={selected?.sid === b.sid ? "true" : undefined}
                  onClick={() => loadBhajan(b)}
                >
                  <span className="vachan-name">{b.subtitle}</span>
                  <span className="vachan-name-guj">{b.title}</span>
                </li>
              ))}
            </ul>
          </aside>

          <section className="vachanamrut-content-panel">
            {!selected && (
              <div className="vachanamrut-placeholder">
                <p>કીર્તન વાંચવા માટે ડાબી બાજુથી ભજન પસંદ કરો.</p>
                <p>Select a Kirtan from the list to read.</p>
              </div>
            )}

            {selected && (
              <article className="vachanamrut-article">
                <h2 className="vachan-title">
                  <Highlighted
                    text={selected.title || ""}
                    word={contentHighlightWord}
                  />
                </h2>
                <p className="header-subtitle">
                  <Highlighted
                    text={selected.subtitle || ""}
                    word={contentHighlightWord}
                  />
                </p>
                {hasAudio && (
                  <div className="kirtan-audio-bar">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio
                      controls
                      src={selected.song}
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
                <ThemedReaderFrame
                  ref={iframeRef}
                  key={selected.sid}
                  src={iframeSrc}
                  title={selected.subtitle}
                  className="kirtan-iframe"
                  sandbox="allow-same-origin"
                  onLoad={() => applyHighlightToIframe(contentHighlightWord)}
                />
              </article>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
